/**
 * Backend URL Configuration Utility
 * 
 * This utility ensures no hardcoded backend URLs exist in the codebase.
 * All backend API calls should use this utility to get the correct URL
 * based on the environment.
 * 
 * Environment variables are set at build time for React apps.
 * - Development: Uses .env.development or .env.local
 * - Production: Uses .env.production
 * 
 * If REACT_APP_BACKEND_URL is not set, this will throw an error
 * to prevent silent failures in production.
 */

/**
 * Get the backend API base URL
 * @throws {Error} If REACT_APP_BACKEND_URL is not set
 */
export const getBackendUrl = (): string => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  if (backendUrl) return backendUrl;
  if (process.env.NODE_ENV !== 'production') {
    return 'http://localhost:3001';
  }
  throw new Error(
    'REACT_APP_BACKEND_URL is not set in production. Set it in your build configuration.'
  );
};

/**
 * Get the backend API URL with /api prefix
 * In dev with HTTPS (e.g. https://localhost:3000), use relative /api so the CRA proxy
 * forwards to the backend and mixed content (HTTPS→HTTP) is avoided.
 * @throws {Error} If REACT_APP_BACKEND_URL is not set (production)
 */
export const getApiUrl = (): string => {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) return apiUrl.replace(/\/$/, '');
  // Dev + HTTPS: use relative path so request goes to same origin and proxy forwards to backend
  if (
    process.env.NODE_ENV !== 'production' &&
    typeof window !== 'undefined' &&
    window.location?.protocol === 'https:'
  ) {
    return '/api';
  }
  const backendUrl = getBackendUrl();
  return `${backendUrl.replace(/\/$/, '')}/api`;
};

/**
 * Check if we're in development mode
 */
export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development' || 
         process.env.REACT_APP_ENVIRONMENT === 'development';
};

/**
 * Check if we're in production mode
 */
export const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production' || 
         process.env.REACT_APP_ENVIRONMENT === 'production';
};

/**
 * Get OAuth redirect URI for a given endpoint
 * @param endpoint - The OAuth callback endpoint (e.g., 'quickbooks/oauth-callback')
 * @param envVar - Optional environment variable name (e.g., 'REACT_APP_QUICKBOOKS_REDIRECT_URI')
 * @throws {Error} If backend URL is not set
 */
export const getOAuthRedirectUri = (endpoint: string, envVar?: string): string => {
  // First check if a specific env var is set
  if (envVar) {
    const envValue = process.env[envVar];
    if (envValue) {
      return envValue;
    }
  }
  
  // Otherwise, construct from backend URL
  const backendUrl = getBackendUrl();
  return `${backendUrl}/api/${endpoint}`;
};

